/**
 * Plain-English rendering of a draft — the human sentence shown live so a
 * non-technical author always sees what the rule means.
 */
import { isAdvancedOp } from "./ruleModel";

const OP_WORDS = {
  eq: "is", neq: "is not", gt: "is more than", gte: "is at least",
  lt: "is less than", lte: "is at most", in_list: "is one of", not_in_list: "is not one of",
};
const HIST_OP_WORDS = {
  gte: "at least", gt: "more than", lte: "at most", lt: "fewer than", eq: "exactly", neq: "not exactly",
};

const labelFor = (meta, dict, key) => meta?.[dict]?.[key]?.label || key;
const schemeName = (wallets, id) => (wallets || []).find((w) => String(w.scheme_id) === String(id))?.name || `scheme ${id}`;

function condText(c, meta, wallets) {
  if (!c) return "…";
  if (c.type === "AND" || c.type === "OR") {
    const joiner = c.type === "AND" ? " AND " : " OR ";
    const inner = (c.children || []).map((ch) => condText(ch, meta, wallets)).join(joiner);
    return `(${c.type === "AND" ? "ALL of" : "ANY of"}: ${inner || "…"})`;
  }
  if (c.type === "fact_check") {
    const lbl = labelFor(meta, "fact_check_keys", c.key);
    const op = OP_WORDS[c.operator] || c.operator || "?";
    let val = Array.isArray(c.value) ? c.value.join(", ") : c.value;
    if (c.key === "metadata.scheme_id" || c.key === "metadata.scheme_ids") val = Array.isArray(c.value) ? c.value.map((v) => schemeName(wallets, v)).join(", ") : schemeName(wallets, c.value);
    return `${lbl} ${op} ${val ?? "…"}`;
  }
  if (c.type === "historical_aggregate") {
    const lbl = labelFor(meta, "historical_keys", c.tracking_key);
    if (c.operator === "first_N_scans") return `it's among the user's first ${c.value} of ${lbl}`;
    if (c.operator === "active_streak") return `the user has a ${c.value}-day ${lbl} streak`;
    if (c.operator === "all_of") return `the user completed all of: ${(c.value || []).join(", ")}`;
    const agg = c.aggregate_type === "sum" ? "total" : "count of";
    const win = isAdvancedOp(c.operator) ? "" : ` in the last ${c.time_window_days} days`;
    return `${agg} ${lbl} is ${HIST_OP_WORDS[c.operator] || c.operator} ${c.value}${win}`;
  }
  return "…";
}

function actionText(a, wallets) {
  const wallet = a.wallet_type === "default" ? "Main wallet" : `${schemeName(wallets, a.wallet_type)} wallet`;
  if (a.type === "CREDIT") return `give ${a.amount || "…"} points to the ${wallet}`;
  if (a.type === "CREDIT_DYNAMIC") return `give ${a.multiplier ? a.multiplier * 100 + "%" : "…"} of ${a.base_field || "…"} to the ${wallet}`;
  if (a.type === "UPDATE_USER_SLAB") return `set tier "${a.slab_name || "…"}"`;
  return "…";
}

const MODE_WORDS = { one_time: "once per user", repeating: "every time", milestone: "at each milestone" };

export function plainEnglish(draft, meta, wallets) {
  if (!draft.event_type) return "Pick an event to start.";
  const ev = meta?.event_types?.find((e) => e.name === draft.event_type)?.label || draft.event_type;
  const when = draft.condition ? condText(draft.condition, meta, wallets) : "…";
  const acts = (draft.actions || []).map((a) => actionText(a, wallets)).join(", and ");
  return `On ${ev}, when ${when}, ${acts || "…"} — ${MODE_WORDS[draft.reward_mode] || ""}.`;
}
